/**
 * CompanyLiveLayers — Overlay Leaflet dedicado ao marshall de empresa.
 *
 * Renderiza no RouteMap:
 *   • 🚨 SOS activos com heartbeat halo pulsante
 *   • 👥 Localização actual dos membros da empresa a navegar (avatar + heading)
 *
 * Só chama endpoints se `enabled` e `companyId` estiverem presentes — user
 * comum recebe 403 do backend e não vê nada.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Marker, Popup, CircleMarker } from 'react-leaflet';
import L from 'leaflet';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const sosIcon = () => L.divIcon({
  html: `<div style="
    width:52px;height:52px;border-radius:50%;
    background:#E13B3B;border:3px solid #fff;
    display:flex;align-items:center;justify-content:center;
    font-size:22px;
    box-shadow:0 0 0 6px rgba(225,59,59,0.35),0 4px 12px rgba(0,0,0,.45);
    animation:pulseSos 1.4s ease-in-out infinite;
  ">🚨</div>`,
  className: 'company-sos-icon',
  iconSize: [52, 52], iconAnchor: [26, 26],
});

const memberIcon = (name, heading) => {
  const initials = (name || '?').split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase();
  const rot = typeof heading === 'number' ? heading : 0;
  return L.divIcon({
    html: `
      <div style="position:relative;width:40px;height:40px;">
        <div style="
          position:absolute;top:-4px;left:10px;width:20px;height:10px;
          background:#E87A2A;clip-path:polygon(50% 0%, 100% 100%, 0% 100%);
          transform:rotate(${rot}deg);transform-origin:50% 100%;
        "></div>
        <div style="
          width:38px;height:38px;border-radius:50%;
          background:#E87A2A;border:2px solid #fff;
          display:flex;align-items:center;justify-content:center;
          color:#fff;font-weight:900;font-size:11px;letter-spacing:.5px;
          box-shadow:0 3px 8px rgba(0,0,0,.4);
        ">${initials}</div>
      </div>
    `,
    className: 'company-member-icon',
    iconSize: [40, 40], iconAnchor: [20, 20],
  });
};

const fmtAgo = (iso) => {
  if (!iso) return '';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${Math.round(s)}s`;
  if (s < 3600) return `${Math.round(s / 60)}min`;
  return `${Math.round(s / 3600)}h`;
};

const CompanyLiveLayers = ({ companyId, enabled = false, refreshMs = 15000 }) => {
  const [sosList, setSosList] = useState([]);
  const [members, setMembers] = useState([]);

  useEffect(() => {
    if (!enabled || !companyId) return undefined;
    let cancelled = false;
    const fetchLive = async () => {
      try {
        const [sosRes, memRes] = await Promise.all([
          axios.get(`${API}/companies/${companyId}/sos-live`, { withCredentials: true }),
          axios.get(`${API}/companies/${companyId}/members-live`, { withCredentials: true }),
        ]);
        if (cancelled) return;
        setSosList(sosRes.data?.sos_active || []);
        setMembers(memRes.data?.locations || []);
      } catch {
        // silencioso — user sem permissão
      }
    };
    fetchLive();
    const id = setInterval(fetchLive, refreshMs);
    return () => { cancelled = true; clearInterval(id); };
  }, [enabled, companyId, refreshMs]);

  if (!enabled) return null;

  return (
    <>
      {/* SOS ATIVOS — heartbeat halo pulsante */}
      {sosList
        .filter((s) => typeof s.lat === 'number' && typeof s.lon === 'number')
        .map((s) => (
          <React.Fragment key={s.sos_id}>
            <CircleMarker
              center={[s.lat, s.lon]}
              radius={38}
              pathOptions={{
                color: '#DC2626', weight: 2, opacity: 0.5,
                fillOpacity: 0.15, fillColor: '#DC2626',
              }}
            />
            <CircleMarker
              center={[s.lat, s.lon]}
              radius={20}
              pathOptions={{
                color: '#DC2626', weight: 0,
                fillOpacity: 0.35, fillColor: '#DC2626',
              }}
            />
            <Marker position={[s.lat, s.lon]} icon={sosIcon()}>
              <Popup>
                <div className="text-sm min-w-[220px]">
                  <div className="font-black text-red-700 mb-1">🚨 SOS EM CURSO</div>
                  <div className="text-slate-800"><b>{s.user_name || 'Membro'}</b></div>
                  {s.message && (
                    <div className="text-slate-600 italic mt-1 text-xs">
                      &ldquo;{s.message}&rdquo;
                    </div>
                  )}
                  <div className="text-slate-500 text-xs mt-2">
                    📱 {s.battery != null ? `${Math.round(s.battery)}% bateria · ` : ''}
                    há {fmtAgo(s.created_at)}
                  </div>
                  {s.route_name && (
                    <div className="text-slate-500 text-xs mt-1">
                      🛣️ {s.route_name}
                    </div>
                  )}
                </div>
              </Popup>
            </Marker>
          </React.Fragment>
        ))}

      {/* MEMBROS AO VIVO — posição actual + heading */}
      {members
        .filter((m) => typeof m.lat === 'number' && typeof m.lon === 'number')
        .map((m) => (
          <Marker
            key={m.user_id}
            position={[m.lat, m.lon]}
            icon={memberIcon(m.name, m.heading)}
          >
            <Popup>
              <div className="text-sm min-w-[180px]">
                <div className="font-bold text-slate-800">👤 {m.name}</div>
                <div className="text-xs text-slate-500 mt-1">
                  {m.speed != null && <>🚗 {Math.round((m.speed || 0) * 3.6)} km/h · </>}
                  atualizado há {fmtAgo(m.updated_at)}
                </div>
                <div className="text-[10px] text-slate-400 mt-1">
                  {m.lat.toFixed(5)}, {m.lon.toFixed(5)}
                </div>
              </div>
            </Popup>
          </Marker>
        ))}
    </>
  );
};

export default CompanyLiveLayers;
